import { useState, useRef, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  BUTTON_ADD_SELECTED_CONTENT,
  BUTTON_CONTINUE,
  BUTTON_GO_BACK,
  GALLERY_SOURCE_CANVA,
  LOADING_CONTENT,
  MODAL_GALLERY_ARIA_LABEL,
  MODAL_GALLERY_EMPTY_CONTENT,
  MODAL_CANVA_ACCOUNT_LABEL,
  MODAL_CANVA_FOLDER_LABEL,
  MODAL_CANVA_IMPORT_IMAGES_ONLY_WARNING_DESCRIPTION,
  PROCESSING_MEDIA_TO_USE,
  SAVING_MEDIA,
  WARNING,
} from "../../i18n/keysTranslations";
import { BUTTON_SHADOW } from "../../utils/colors";
import { useTranslationApp } from "../../lib/i18next";
import useNearScreen from "../../hooks/useNearScreen";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useDebounced } from "../../hooks/useDebounce";
import ModalBasicLayout from "./ModalBasicLayout";
import Loader from "../Loaders/Loader";
import Button from "../Buttons/Button";
import Gallery from "../Gallery/Gallery";
import { usePagination } from "../../hooks/usePagination";
import { CANVA, IMAGE_TYPE_SQUARE } from "../../utils/constants";
import { useRebrandBanner } from "../../hooks/useRebrandBanner";
import { startGetGalleryAdsPlatforms } from "../../actions/adsCampaigns";
import {
  startAddContentsToList,
  startSaveMediaInStorage,
  startExportCanvaDesign,
} from "../../actions/user";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import SelectCanvaAccountWithConnect from "../Canva/SelectCanvaAccountWithConnect";
import SelectCanvaFolder from "../Canva/SelectCanvaFolder";
import { ConfirmAlert } from "../Alerts/Alerts";

import { LaunchError } from "../../utils/errors";

const PAGE_SIZE = 10;

const ModalGalleryCanva = ({
  modalOpen,
  multiple = false,
  onCloseModal,
  onCallbackSelectedItems,
  pathStorage,
  contentFolderID = null,
  integrationID: integrationIDProp,
  compact = false,
}) => {
  const [data, setData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadLastItem, setLoadLastItem] = useState(false);
  const [isCalling, setIsCalling] = useState(false);

  const { t } = useTranslationApp();

  const methods = useForm({
    defaultValues: { canvaAccount: null, canvaFolder: null },
  });
  const canvaAccount = methods.watch("canvaAccount");
  const canvaFolder = methods.watch("canvaFolder");
  const selectedIntegrationID =
    canvaAccount?.id || canvaAccount?.integrationID || integrationIDProp;
  const selectedFolderID = canvaFolder?.id || undefined;

  const galleryRef = useRef(null);

  const { isNearScreen, fromRef } = useNearScreen({
    distance: "100px",
    once: false,
    disabledTimeout: true,
  });

  const dispatch = useDispatchApp();
  const { totalTopOffsetPx } = useRebrandBanner();

  const integrationNotFoundModalOpen = useSelectorApp(
    (s) => s?.alerts?.integrationNotFound?.open
  );
  const prevIntegrationNotFoundModalOpen = useRef(integrationNotFoundModalOpen);

  const getInitialData = async () => {
    if (!selectedIntegrationID) return;
    setLoading(true);
    setData([]);
    setSelectedItems([]);
    setLoadLastItem(false);
    onChangeLastKey(null);
    const response = await dispatch(
      startGetGalleryAdsPlatforms({
        platform: CANVA,
        integrationID: selectedIntegrationID,
        adAccountID: undefined,
        pageID: undefined,
        lastKey: undefined,
        folderID: selectedFolderID,
        onChangeLastKey,
        onChangeLoadLastItem: setLoadLastItem,
        onChangeIsCalling: setIsCalling,
        limit: PAGE_SIZE,
        from: CANVA,
      })
    );

    if (response.ok) {
      const dataFormatted = response?.data?.map((media) => ({
        ...media,
        id: media.id,
      }));
      setData(dataFormatted);
    }

    setLoading(false);
  };

  const getNextMedia = useDebounced(async () => {
    if (loadLastItem || isCalling || !selectedIntegrationID) return;

    const response = await dispatch(
      startGetGalleryAdsPlatforms({
        platform: CANVA,
        integrationID: selectedIntegrationID,
        adAccountID: undefined,
        pageID: undefined,
        lastKey,
        folderID: selectedFolderID,
        onChangeLastKey,
        onChangeLoadLastItem: setLoadLastItem,
        onChangeIsCalling: setIsCalling,
        limit: PAGE_SIZE,
        from: CANVA,
      })
    );
    if (response.ok) {
      const dataFormatted = response?.data?.map((media) => ({
        ...media,
        id: media.id,
      }));
      setData((prevState) => [...prevState, ...dataFormatted]);
    }
  }, 1000);

  const { lastKey, onChangeLastKey } = usePagination({
    rowsPerPageValue: PAGE_SIZE,
    onCallBackNextPage: getNextMedia,
  });

  const onCanvaAccountChange = () => {
    methods.setValue("canvaFolder", null);
  };

  useEffect(() => {
    if (modalOpen && selectedIntegrationID) {
      getInitialData();
    }
    // eslint-disable-next-line
  }, [modalOpen, selectedIntegrationID, selectedFolderID]);

  useEffect(() => {
    if (isNearScreen && !loadLastItem) getNextMedia();
    //eslint-disable-next-line
  }, [isNearScreen]);

  useEffect(() => {
    const wasOpen = prevIntegrationNotFoundModalOpen.current;
    const isNowClosed = wasOpen && !integrationNotFoundModalOpen;

    if (isNowClosed && modalOpen) {
      getInitialData();
    }

    prevIntegrationNotFoundModalOpen.current = integrationNotFoundModalOpen;
    //eslint-disable-next-line
  }, [integrationNotFoundModalOpen, modalOpen]);

  const onFailedLoadUrl = (index) => {
    setData((prevState) => {
      const newData = [...prevState];
      const itemToRemove = newData[index];
      if (itemToRemove?.id) {
        setSelectedItems((prevSelected) =>
          prevSelected.filter((id) => id !== itemToRemove.id)
        );
      }
      const filteredData = newData.filter((item, i) => i !== index);
      return filteredData;
    });
  };

  const onSelectItem = (index) => {
    const item = data[index];
    if (!item || !item.id) return;

    const itemId = item.id;

    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(itemId)) {
        return prevSelected.filter((id) => id !== itemId);
      } else {
        if (multiple) {
          return [...prevSelected, itemId];
        } else {
          return [itemId];
        }
      }
    });
  };

  const onSendSelectedItems = async (indexes) => {
    const dataSelectedItems = [];
    const itemsToProcess = indexes
      ? indexes.map((index) => data[index]).filter(Boolean)
      : selectedItems
          .map((id) => data.find((item) => item.id === id))
          .filter(Boolean);

    itemsToProcess.forEach((item) => {
      if (item) {
        dataSelectedItems.push({
          url: item.url,
          from: item.from || "gallery",
          id: item.id,
          userID: item.userID || "",
        });
      }
    });

    setIsUploading(true);

    try {
      const urlsWithIds = [];
      for (const item of dataSelectedItems) {
        if (item.id) {
          const exportResponse = await dispatch(
            startExportCanvaDesign({
              designID: item.id,
              contentType: "image",
              orientation: IMAGE_TYPE_SQUARE,
              integrationID: selectedIntegrationID,
            })
          );

          if (exportResponse) {
            const imageUrl = exportResponse[0];
            urlsWithIds.push({
              ...item,
              url: imageUrl,
            });
          } else {
            urlsWithIds.push(item);
          }
        } else {
          urlsWithIds.push(item);
        }
      }

      const urls = urlsWithIds.map((item) => item.url);

      const response = await dispatch(
        startSaveMediaInStorage({
          urls,
          path: pathStorage,
        })
      );

      if (!response.ok) {
        setIsUploading(false);
        return false;
      }

      const urlsStored = response.data.urls;

      urlsStored.forEach((urlStored, index) => {
        urlsWithIds[index].url = urlStored;
      });
      await dispatch(
        startAddContentsToList(
          urlsStored.map((url) => ({
            url,
            type: "upload",
            ...(contentFolderID && { folderID: contentFolderID }),
          }))
        )
      );

      setIsUploading(false);
      onCallbackSelectedItems(urlsWithIds);
      onCloseModal();
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onSendSelectedItems",
          capture: "exception",
        })
      );
      console.error("Error al procesar los elementos seleccionados:", error);
      setIsUploading(false);
      return false;
    }
  };

  const onConfirmSendSelectedItems = async () => {
    const result = await ConfirmAlert({
      title: t(WARNING),
      text: t(MODAL_CANVA_IMPORT_IMAGES_ONLY_WARNING_DESCRIPTION),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t(BUTTON_CONTINUE),
      cancelButtonText: t(BUTTON_GO_BACK),
    });

    if (!result.isConfirmed) return;

    await onSendSelectedItems();
  };

  const dataFiltered = data;

  const selectedItemsAsIndexes = useMemo(() => {
    return dataFiltered
      .map((item, index) =>
        item?.id && selectedItems.includes(item.id) ? index : null
      )
      .filter((index) => index !== null);
  }, [dataFiltered, selectedItems]);

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={compact ? true : !compact}
      fullScreen={compact ? false : !compact}
      maxWidth={compact ? "lg" : "xl"}
      scroll="paper"
      aria-label={t(MODAL_GALLERY_ARIA_LABEL)}
      sxContent={{
        p: 0,
        pl: 3,
        pb: 2,
        mt: 1,
        overflowX: "hidden",
        "::-webkit-scrollbar": {
          width: 0,
          height: 0,
          background: "transparent",
        },
      }}
      refContent={galleryRef}
      title={t(GALLERY_SOURCE_CANVA)}
    >
      <FormProvider {...methods}>
        <Box
          sx={{
            mb: 3,
            pr: 3,
            display: "flex",
            flexDirection: "row",
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SelectCanvaAccountWithConnect
              control={methods.control}
              label={t(MODAL_CANVA_ACCOUNT_LABEL)}
              name="canvaAccount"
              fullWidth
              variant="filled"
              errors={methods.formState.errors}
              onChangeAction={onCanvaAccountChange}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SelectCanvaFolder
              control={methods.control}
              name="canvaFolder"
              label={t(MODAL_CANVA_FOLDER_LABEL)}
              integrationID={selectedIntegrationID}
              disabled={!selectedIntegrationID}
              fullWidth
              variant="filled"
              errors={methods.formState.errors}
            />
          </Box>
        </Box>
        {loading && (
          <Loader size={70} hasMessage={true} message={t(LOADING_CONTENT)} />
        )}
        {!loading && (
          <Gallery
            data={dataFiltered}
            onCloseModal={onCloseModal}
            lastItemRef={fromRef}
            loadLastItem={loadLastItem}
            onFailedLoadUrl={onFailedLoadUrl}
            onSelectItem={onSelectItem}
            selectedItems={selectedItemsAsIndexes}
            onSelectPreview={() => {}}
            multiple={multiple}
            showDimensionsOverlay={true}
            showFiltersSelector={false}
            showActionButtons={false}
            sxGallery={{
              px: 0,
            }}
          />
        )}
        {!loading && dataFiltered?.length === 0 && loadLastItem && (
          <Box
            sx={{
              height: `calc(100% - ${totalTopOffsetPx}px)`,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ml: "-12px",
              mb: 4,
            }}
          >
            <Typography variant="h4" align="center">
              {t(MODAL_GALLERY_EMPTY_CONTENT)}
            </Typography>
          </Box>
        )}

        {selectedItems.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: isUploading ? "#FFF" : "transparent",
              position: "sticky",
              bottom: 0,
              zIndex: 2,
              py: 1,
              mt: 2,
              pb: 2,
            }}
          >
            {isUploading && (
              <LinearLoaderWithTime
                labels={[t(PROCESSING_MEDIA_TO_USE), t(SAVING_MEDIA)]}
                time={30 * 1000}
              />
            )}

            {!isUploading && (
              <Button
                color="primary"
                sx={{
                  boxShadow: BUTTON_SHADOW,
                  "&:hover": {
                    boxShadow: BUTTON_SHADOW,
                  },
                }}
                onClick={(e) => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  onConfirmSendSelectedItems();
                }}
              >
                {t(BUTTON_ADD_SELECTED_CONTENT)}
              </Button>
            )}
          </Box>
        )}
      </FormProvider>
    </ModalBasicLayout>
  );
};

export default ModalGalleryCanva;
